/**
 * Wave 3.16 — Jewellery detail page.
 *
 * Hero image + full bill of material breakdown. Provides the Print BOM
 * PDF action and an Edit Image action (Phase C — image-edit endpoint
 * already exists; this UI lets operators swap a missing/wrong image
 * after the receipt was committed).
 */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import AppShell from '@/components/AppShell';
import CompanyGate from '@/components/CompanyGate';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  ArrowLeft, FileText, Image as ImageIcon, ScrollText, Hammer, Diamond,
  Sparkles, Circle, Coins, Upload, CalendarDays, IndianRupee, Scale, XCircle, Wrench, StickyNote, Pencil, Check, X, Loader2,
} from 'lucide-react';
import { formatINR, formatCarats, formatGrams, formatDate } from '@/lib/format';
import { toast } from 'sonner';
import { fileToResizedDataUrl } from '@/lib/imageResize';
import { extractError } from '@/lib/errors';
import { PdfDownloadDialog } from '@/components/phase11/PdfDownloadDialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
// Wave 3.51 Phase B — page-level Request Edit (top-level only) + lock banner.
import { RequestEditDialog } from '@/components/RequestEditDialog';
// Wave 3.99k — Sticky section navigator + back-to-top pill on long pages.
import { StickySectionNav } from '@/components/StickySectionNav';
import { ScrollToTopButton } from '@/components/ScrollToTopButton';
// Wave 3.99n — Section tint helper.
import { sectionMat } from '@/lib/sectionTints';
import { PacketEditLockBanner } from '@/components/edit-request/PacketEditLockBanner';

const MAT_ICON = {
  diamond: Diamond, color_stone: Sparkles, pearl: Circle, gold: Coins,
};
const MAT_LABEL = {
  diamond: 'Diamond', color_stone: 'Colour Stone', pearl: 'Pearl', gold: 'Gold',
};
// Wave 3.23 — canonical hierarchy for Raw Material / Overflow tables.
// Gold first, then Diamond, then Colour Stone, then Pearl. Same order
// the BOM PDF + Goldsmith Detail by-order tables use.
const MATERIAL_RANK = { gold: 0, diamond: 1, color_stone: 2, pearl: 3 };
const sortByMaterial = (arr) => [...(arr || [])].sort(
  (a, b) => (MATERIAL_RANK[a?.material_type] ?? 99) - (MATERIAL_RANK[b?.material_type] ?? 99)
);

export default function JewelleryDetail() {
  const navigate = useNavigate();
  return (
    <AppShell
      title="Jewellery Detail"
      right={
        <Button
          variant="outline"
          onClick={() => navigate('/jewellery')}
          className="h-9"
          data-testid="jewellery-detail-back"
        >
          <ArrowLeft size={14} className="mr-1" /> Back
        </Button>
      }
    >
      <CompanyGate>
        <JewelleryDetailInner />
      </CompanyGate>
      {/* Wave 3.99j — Global back-to-top pill. */}
      <ScrollToTopButton />
    </AppShell>
  );
}

function JewelleryDetailInner() {
  const { id } = useParams();
  const navigate = useNavigate();
  // Wave 3.41 — Restricted users hide cost, labour, and settlement totals.
  const { effectiveCompanyId, canViewValues, isSuperAdmin, isCompanyAdmin } = useAuth();
  const canEditNo = isSuperAdmin || isCompanyAdmin;
  const [j, setJ] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfOpen, setPdfOpen] = useState(false);
  // Wave 3.29 — BOM RML form toggle (Collapsed = summary, Expanded =
  // per-packet drill-down ledger). Persisted to localStorage so the
  // operator's preference is remembered between sessions.
  const [bomRmlForm, setBomRmlForm] = useState(() => {
    try {
      const v = localStorage.getItem('bom.rml_form');
      return v === 'expanded' ? 'expanded' : 'collapsed';
    } catch {
      return 'collapsed';
    }
  });
  useEffect(() => {
    try { localStorage.setItem('bom.rml_form', bomRmlForm); } catch { /* noop */ }
  }, [bomRmlForm]);
  const [uploadingImage, setUploadingImage] = useState(false);
  // Inline jewellery-number edit (Company Admin + Super Admin).
  const [editingNo, setEditingNo] = useState(false);
  const [noDraft, setNoDraft] = useState('');
  const [savingNo, setSavingNo] = useState(false);

  const saveNo = async () => {
    const v = (noDraft || '').trim();
    if (!v) { toast.error('Jewellery number cannot be empty'); return; }
    if (v === (j?.jewellery_no || '')) { setEditingNo(false); return; }
    setSavingNo(true);
    try {
      const res = await api.patch(`/jewellery/${j.id}/number`, { jewellery_no: v });
      setJ((prev) => (prev ? { ...prev, jewellery_no: res?.data?.jewellery_no || v } : prev));
      toast.success('Jewellery number updated');
      setEditingNo(false);
    } catch (e) {
      toast.error(extractError(e, 'Failed to update jewellery number'));
    } finally {
      setSavingNo(false);
    }
  };
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/jewellery/${id}`);
      setJ(res.data);
    } catch (e) {
      toast.error(extractError(e, 'Failed to load jewellery'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
     
  }, [id]);

  const handleImageFile = async (file) => {
    if (!file) return;
    setUploadingImage(true);
    try {
      const data_url = await fileToResizedDataUrl(file);
      await api.post(`/jewellery/${id}/image`, { image_data_url: data_url });
      toast.success('Jewellery image updated');
      await load();
    } catch (e) {
      toast.error(extractError(e, 'Failed to update image'));
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const res = await api.post(`/jewellery/${id}/cancel`);
      toast.success(
        `Jewellery cancelled. ${res.data.restored_stage_to ? `Order restored to "${res.data.restored_stage_to}".` : ''}`,
      );
      // Navigate back to the source order (or list if not available).
      if (j?.source_order_id) {
        navigate(`/orders/${j.source_order_id}`);
      } else {
        navigate('/jewellery');
      }
    } catch (e) {
      toast.error(extractError(e, 'Failed to cancel jewellery'));
    } finally {
      setCancelling(false);
      setCancelOpen(false);
    }
  };

  if (loading || !j) {
    return <div className="text-slate-500 py-10" data-testid="jewellery-detail-loading">Loading…</div>;
  }

  const img = j.image || j.fallback_image;
  const isFallbackImg = !j.image && j.fallback_image;
  const comp = j.components || {};
  const hasStones = (comp.diamond_carats || comp.color_stone_carats || comp.pearl_carats) > 0
    || (comp.diamond_pieces || comp.color_stone_pieces || comp.pearl_pieces) > 0;

  return (
    <div className="space-y-4" data-testid="jewellery-detail-page">
      {/* Hero header */}
      <section id="jd-overview" className={sectionMat('teal')}>
      <Card className="p-5">
        <div className="flex flex-wrap gap-5">
          <div className="relative w-44 h-44 rounded-lg overflow-hidden bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0 group">
            {img ? (
              <img
                src={img}
                alt={j.jewellery_no}
                className={`w-full h-full object-cover ${isFallbackImg ? 'opacity-80' : ''}`}
                data-testid="jewellery-detail-image"
              />
            ) : (
              <ImageIcon size={32} className="text-slate-300" />
            )}
            {isFallbackImg && (
              <div className="absolute top-2 right-2">
                <Badge variant="outline" className="bg-white/90 text-[9px] font-normal">
                  Order ref
                </Badge>
              </div>
            )}
            {/* Edit overlay */}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingImage}
              className="absolute inset-x-0 bottom-0 bg-black/55 backdrop-blur-sm text-white text-[11px] py-1.5 flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
              data-testid="jewellery-detail-edit-image-button"
            >
              <Upload size={11} />
              {uploadingImage ? 'Uploading…' : (j.image ? 'Replace image' : 'Add image')}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => handleImageFile(e.target.files?.[0])}
            />
          </div>
          <div className="flex-1 min-w-[280px]">
            <div className="flex flex-wrap items-center gap-2">
              {editingNo ? (
                <div className="flex items-center gap-1.5">
                  <input
                    value={noDraft}
                    onChange={(e) => setNoDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); saveNo(); }
                      if (e.key === 'Escape') { e.preventDefault(); setEditingNo(false); }
                    }}
                    autoFocus
                    disabled={savingNo}
                    className="font-mono text-xl text-emerald-700 border border-[var(--jewellery-border)] rounded px-2 py-0.5 w-[200px] focus:outline-none focus:ring-2 focus:ring-[var(--jewellery-icon)]"
                    data-testid="jewellery-detail-no-input"
                  />
                  <button
                    type="button"
                    onClick={saveNo}
                    disabled={savingNo}
                    title="Save"
                    aria-label="Save jewellery number"
                    data-testid="jewellery-detail-no-save"
                    className="inline-flex items-center justify-center w-8 h-8 rounded-md text-emerald-700 hover:bg-emerald-50 transition-colors disabled:opacity-50"
                  >
                    {savingNo ? <Loader2 size={16} className="animate-spin" /> : <Check size={17} />}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingNo(false)}
                    disabled={savingNo}
                    title="Cancel"
                    aria-label="Cancel editing"
                    data-testid="jewellery-detail-no-cancel"
                    className="inline-flex items-center justify-center w-8 h-8 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors disabled:opacity-50"
                  >
                    <X size={17} />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-1.5">
                  <div className="font-mono text-xl text-emerald-700" data-testid="jewellery-detail-no">
                    {j.jewellery_no}
                  </div>
                  {canEditNo && (
                    <button
                      type="button"
                      onClick={() => { setNoDraft(j.jewellery_no || ''); setEditingNo(true); }}
                      title="Edit jewellery number"
                      aria-label="Edit jewellery number"
                      data-testid="jewellery-detail-no-edit"
                      className="inline-flex items-center justify-center w-7 h-7 rounded-md text-slate-400 hover:text-[var(--jewellery-icon)] hover:bg-[var(--jewellery-bg)] transition-colors"
                    >
                      <Pencil size={14} />
                    </button>
                  )}
                </div>
              )}
              <Badge
                variant="outline"
                className={`text-[10px] ${j.status === 'received' ? 'border-emerald-300 text-emerald-700 bg-emerald-50' : 'border-slate-300 text-slate-600'}`}
              >
                {j.status}
              </Badge>
              {/* Wave 3.25 — Type of Jewellery surfaced next to the No
                  so it's the first thing users see on the detail page. */}
              {j.jewellery_type && (
                <Badge
                  variant="outline"
                  className="text-[10px] bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)] border-[var(--jewellery-icon)]/30"
                  data-testid="jewellery-detail-type-badge"
                >
                  {j.jewellery_type}
                </Badge>
              )}
            </div>
            <div className="text-lg text-[var(--ink-navy)] mt-1">{j.title || '(untitled)'}</div>
            {j.description ? (
              <div className="text-xs text-slate-600 mt-1">{j.description}</div>
            ) : null}
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <Stat
                label="Order"
                value={
                  <Link
                    to={`/orders/${j.source_order_id}`}
                    className="font-mono text-emerald-700 hover:underline"
                    data-testid="jewellery-detail-order-link"
                  >
                    {j.source_order_no || '—'}
                  </Link>
                }
                icon={<ScrollText size={12} />}
              />
              <Stat label="Goldsmith" value={j.goldsmith_name || '—'} icon={<Hammer size={12} />} />
              <Stat
                label="Received"
                value={formatDate(j.received_at)}
                icon={<CalendarDays size={12} />}
              />
              <Stat
                label="Net Weight"
                value={`${formatGrams(j.net_weight_grams)} g`}
                icon={<Scale size={12} />}
                mono
                highlight
              />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <Button
              className="luxury-button"
              onClick={() => setPdfOpen(true)}
              data-testid="jewellery-detail-bom-pdf"
            >
              <FileText size={14} className="mr-1.5" /> Print BOM
            </Button>
            <Button
              variant="outline"
              className="border-rose-200 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
              onClick={() => setCancelOpen(true)}
              disabled={j.status !== 'received'}
              data-testid="jewellery-detail-cancel-button"
            >
              <XCircle size={14} className="mr-1.5" /> Cancel Receipt
            </Button>
            {/* Wave 3.51 Phase B — Request Edit (top-level fields only).
                Renders nothing if outside T+1 window or not CA/SA. */}
            <RequestEditDialog
              entityType="jewellery_receive"
              entity={{
                id: j.id,
                created_at: j.received_at || j.created_at,
                company_id: j.company_id,
                reference: j.reference,
                note: j.note,
                jewellery_no: j.jewellery_no,
                order_id: j.source_order_id,
                goldsmith_id: j.goldsmith_id,
              }}
              testId="jewellery-detail-request-edit"
              onSuccess={() => { load(); }}
            />
          </div>
        </div>
      </Card>

      {/* Wave 3.51 Phase B — lock banner for jewellery_receive (scoped via
          source order + goldsmith ids). */}
      <PacketEditLockBanner
        orderId={j.source_order_id}
        goldsmithId={j.goldsmith_id}
      />

      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent data-testid="jewellery-cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel jewellery receipt?</AlertDialogTitle>
            <AlertDialogDescription>
              This will reverse every raw-material movement created by this receipt:
              <ul className="list-disc ml-5 mt-2 space-y-0.5 text-xs">
                <li>Consumed material is returned to the order&apos;s attribution.</li>
                <li>Overflow that went to the goldsmith&apos;s unattributed pool will be subtracted back from there.</li>
                <li>Overflow that went back to source packets will be debited out of those packets.</li>
                <li>Any Ghisai Loss (write-off) will be reversed — the material returns to the order&apos;s attribution.</li>
                <li>If this receipt auto-completed the order, the order will be restored to <span className="font-mono text-[var(--ink-navy)]">{j.order_stage_at_receipt || 'In Production'}</span>.</li>
                <li>The jewellery record will be deleted.</li>
              </ul>
              <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                If the goldsmith has already consumed any of the unattributed material from this receipt, cancellation will be blocked with a clear error.
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelling} data-testid="jewellery-cancel-no">Keep it</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleCancel(); }}
              disabled={cancelling}
              className="bg-rose-600 hover:bg-rose-700 focus:ring-rose-500"
              data-testid="jewellery-cancel-yes"
            >
              {cancelling ? 'Cancelling…' : 'Yes, cancel & reverse'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </section>

      {/* Wave 3.99k — Sticky section-nav chip bar. Conditionally
          includes Overflow only if there's overflow data, and
          Settlement only if the user can view values.
          Wave 3.99m — Chips now surface small badge counts for
          list-style sections (Raw Material rows and Overflow
          rows). */}
      <StickySectionNav
        sections={[
          { id: 'jd-overview', label: 'Overview', tint: 'teal' },
          { id: 'jd-composition', label: 'Composition', tint: 'sky' },
          {
            id: 'jd-raw-material',
            label: 'Raw Material Used',
            tint: 'violet',
            badge: (j.rm_consumed_snapshot || []).length || undefined,
          },
          ...((j.rm_overflow_snapshot && j.rm_overflow_snapshot.length > 0)
            ? [{
                id: 'jd-overflow',
                label: 'Overflow',
                tint: 'rose',
                badge: j.rm_overflow_snapshot.length,
              }]
            : []),
          ...(canViewValues ? [{ id: 'jd-settlement', label: 'Settlement', tint: 'amber' }] : []),
        ]}
        topOffset={56}
      />

      {/* Composition */}
      <section id="jd-composition" className={sectionMat('sky')}>
      <Card className="p-5">
        <div className="font-medium text-[var(--ink-navy)] mb-3">Stone &amp; Gold composition</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {(comp.diamond_carats || comp.diamond_pieces) ? (
            <Stat
              label="Diamonds"
              value={`${formatCarats(comp.diamond_carats)} ct · ${comp.diamond_pieces || 0} pcs`}
              icon={<Diamond size={12} />}
            />
          ) : (
            <Stat label="Diamonds" value="—" icon={<Diamond size={12} />} muted />
          )}
          {(comp.color_stone_carats || comp.color_stone_pieces) ? (
            <Stat
              label="Colour Stones"
              value={`${formatCarats(comp.color_stone_carats)} ct · ${comp.color_stone_pieces || 0} pcs`}
              icon={<Sparkles size={12} />}
            />
          ) : (
            <Stat label="Colour Stones" value="—" icon={<Sparkles size={12} />} muted />
          )}
          {(comp.pearl_carats || comp.pearl_pieces) ? (
            <Stat
              label="Pearls"
              value={`${formatCarats(comp.pearl_carats)} ct · ${comp.pearl_pieces || 0} pcs`}
              icon={<Circle size={12} />}
            />
          ) : (
            <Stat label="Pearls" value="—" icon={<Circle size={12} />} muted />
          )}
          <Stat
            label={`Gold (${j.gold_purity_name || ''})`}
            value={`${formatGrams(j.net_weight_grams)} g · ${(j.gold_purity_percent || 0).toFixed(2)}%`}
            icon={<Coins size={12} />}
            highlight
          />
          {(j.mountings || []).length > 0 && (
            <Stat
              label="Mountings (gold)"
              value={`${formatGrams((j.mountings || []).reduce((a, m) => a + (m.grams || 0), 0))} g${canViewValues ? ` · ${formatINR((j.mountings || []).reduce((a, m) => a + (m.value || 0), 0))}` : ''}`}
              icon={<Wrench size={12} />}
            />
          )}
        </div>
        {!hasStones && (
          <div className="text-[11px] text-slate-500 mt-3">
            No stones mounted — this is a plain gold piece.
          </div>
        )}
      </Card>
      </section>

      {/* Raw material used */}
      <section id="jd-raw-material" className={sectionMat('violet')}>
      <Card className="p-5">
        <div className="font-medium text-[var(--ink-navy)] mb-3">Raw Material Used</div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Material</TableHead>
                <TableHead>Packet / Purity</TableHead>
                <TableHead>Schema</TableHead>
                <TableHead className="text-right">Carats</TableHead>
                <TableHead className="text-right">Pieces</TableHead>
                <TableHead className="text-right">Grams</TableHead>
                {/* Wave 3.41 — Value column hidden for users without values.view */}
                {canViewValues && <TableHead className="text-right">Value (₹)</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {(() => {
                const rm = j.rm_consumed_snapshot || [];
                const mnts = j.mountings || [];
                if (rm.length === 0 && mnts.length === 0) {
                  return (
                    <TableRow>
                      <TableCell colSpan={canViewValues ? 7 : 6} className="text-center text-slate-500 py-6">
                        No raw material recorded for this piece.
                      </TableCell>
                    </TableRow>
                  );
                }
                const sorted = sortByMaterial(rm);
                const goldRows = sorted.filter((r) => r.material_type === 'gold');
                const nonGoldRows = sorted.filter((r) => r.material_type !== 'gold');
                const renderMaterialRow = (r, key) => {
                  const Icon = MAT_ICON[r.material_type] || Diamond;
                  const isGold = r.material_type === 'gold';
                  return (
                    <TableRow key={key} data-testid={`jewellery-rm-row-${key}`}>
                      <TableCell>
                        <span className="inline-flex items-center gap-1">
                          <Icon size={12} />{MAT_LABEL[r.material_type] || r.material_type}
                        </span>
                      </TableCell>
                      <TableCell className="font-mono">
                        {isGold
                          ? `${r.purity_name || ''} (${(r.purity_percent || 0).toFixed(2)}%)`
                          : (r.packet_no || '—')}
                        {r.issue_note ? (
                          <div
                            className="mt-0.5 flex items-start gap-1 font-sans text-[11px] text-slate-500 italic max-w-[240px] whitespace-normal break-words"
                            title={r.issue_note}
                            data-testid={`jewellery-rm-row-${key}-note`}
                          >
                            <StickyNote size={11} className="mt-[1px] shrink-0 text-slate-400" />
                            <span>{r.issue_note}</span>
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell>{r.classification_schema || '—'}</TableCell>
                      <TableCell className="text-right font-mono">
                        {!isGold ? formatCarats(r.carats || 0) : '—'}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {!isGold ? (r.pieces || 0) : '—'}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {isGold ? formatGrams(r.grams || 0) : '—'}
                      </TableCell>
                      {canViewValues && (
                        <TableCell className="text-right font-mono text-amber-700">
                          {formatINR(r.value || 0)}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                };
                // Wave 3.99ac — Mountings render under Gold, above Diamonds.
                // Value = current gold rate × purity% × weight (server-computed).
                const renderMountingRow = (m, i) => (
                  <TableRow key={`mnt-${m.mounting_id || i}`} data-testid={`jewellery-rm-mounting-row-${i}`} className="bg-amber-50/40">
                    <TableCell>
                      <span className="inline-flex items-center gap-1 text-amber-800">
                        <Wrench size={12} />Mounting
                      </span>
                    </TableCell>
                    <TableCell className="font-mono">
                      {(m.purity_name || m.purity_karat || 'Gold')}
                      {m.purity_percent ? ` (${Number(m.purity_percent).toFixed(2)}%)` : ''}
                    </TableCell>
                    <TableCell>{m.mounting_name || '—'}</TableCell>
                    <TableCell className="text-right font-mono">—</TableCell>
                    <TableCell className="text-right font-mono">—</TableCell>
                    <TableCell className="text-right font-mono">{formatGrams(m.grams || 0)}</TableCell>
                    {canViewValues && (
                      <TableCell className="text-right font-mono text-amber-700">
                        {formatINR(m.value || 0)}
                      </TableCell>
                    )}
                  </TableRow>
                );
                return (
                  <>
                    {goldRows.map((r, i) => renderMaterialRow(r, `g${i}`))}
                    {mnts.map((m, i) => renderMountingRow(m, i))}
                    {nonGoldRows.map((r, i) => renderMaterialRow(r, `n${i}`))}
                  </>
                );
              })()}
            </TableBody>
          </Table>
        </div>
      </Card>
      </section>

      {/* Overflow disposition */}
      {j.rm_overflow_snapshot && j.rm_overflow_snapshot.length > 0 && (
        <section id="jd-overflow" className={sectionMat('rose')}>
        <Card className="p-5">
          <div className="font-medium text-[var(--ink-navy)] mb-3">Overflow disposition</div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Material</TableHead>
                  <TableHead>From</TableHead>
                  <TableHead className="text-right">Carats</TableHead>
                  <TableHead className="text-right">Pieces</TableHead>
                  <TableHead className="text-right">Grams</TableHead>
                  <TableHead>Destination</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortByMaterial(j.rm_overflow_snapshot).map((r, i) => (
                  <TableRow key={i} data-testid={`jewellery-overflow-row-${i}`}>
                    <TableCell>{MAT_LABEL[r.material_type] || r.material_type}</TableCell>
                    <TableCell className="font-mono">
                      {r.packet_no || (r.material_type === 'gold' ? `${(r.purity_percent || 0).toFixed(2)}%` : '—')}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {r.carats != null && r.material_type !== 'gold' ? formatCarats(r.carats) : '—'}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {r.material_type !== 'gold' ? (r.pieces || 0) : '—'}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {r.grams != null ? formatGrams(r.grams) : '—'}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={`text-[10px] ${
                          r.destination === 'return_to_packet'
                            ? 'border-blue-300 text-blue-700 bg-blue-50'
                            : r.destination === 'ghisai_loss'
                              ? 'border-rose-300 text-rose-700 bg-rose-50'
                              : 'border-emerald-300 text-emerald-700 bg-emerald-50'
                        }`}
                      >
                        {r.destination === 'return_to_packet'
                          ? 'Returned to original packet'
                          : r.destination === 'ghisai_loss'
                            ? 'Ghisai Loss (write-off)'
                            : 'Goldsmith Unattributed'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
        </section>
      )}

      {/* Settlement — Wave 3.41 — Entire labour/cost block hidden for users
          without values.view since every figure here is monetary. */}
      {canViewValues && (
      <section id="jd-settlement" className={sectionMat('amber')}>
      <Card className="p-5">
        <div className="font-medium text-[var(--ink-navy)] mb-3">Settlement</div>
        <div className="grid md:grid-cols-3 gap-3 text-sm">
          <Stat label="Gross Weight" value={`${formatGrams(j.gross_weight_grams)} g`} mono icon={<Scale size={12} />} />
          <Stat label="Net Weight" value={`${formatGrams(j.net_weight_grams)} g`} mono highlight icon={<Scale size={12} />} />
          <Stat label="Labour Rate" value={`${formatINR(j.labour_rate_per_gram || 0)} / g`} mono icon={<IndianRupee size={12} />} />
          <Stat label="Labour Amount" value={formatINR(j.labour_amount || 0)} mono highlight icon={<IndianRupee size={12} />} />
          {(j.gold_loss_percent || 0) > 0 ? (
            <>
              <Stat
                label="Gold Loss %"
                value={`${j.gold_loss_percent}% (${j.gold_loss_mode === 'gold_grams' ? 'extra gold' : '₹ on bill'})`}
                mono
              />
              {j.gold_loss_mode === 'gold_grams' ? (
                <Stat
                  label="Gold Loss (extra gold)"
                  value={`${formatGrams(j.gold_loss_grams || 0)} g ${j.gold_purity_name ? `(${j.gold_purity_name})` : ''}`}
                  mono
                  icon={<Coins size={12} />}
                />
              ) : (
                <>
                  <Stat
                    label="Gold Loss (₹)"
                    value={formatINR(j.gold_loss_amount || 0)}
                    mono
                    highlight
                    icon={<IndianRupee size={12} />}
                  />
                  <Stat
                    label="999 Rate at receipt"
                    value={`${formatINR(j.gold_rate_999_at_receipt || 0)} / g`}
                    mono
                  />
                </>
              )}
            </>
          ) : (
            <Stat label="Gold Loss" value="None" mono muted />
          )}
        </div>
        <Separator className="my-4" />
        <div className="flex flex-wrap items-baseline justify-between gap-3" data-testid="jewellery-settlement-totals">
          <div className="text-xs text-slate-500">
            Received {formatDate(j.received_at)} by {j.received_by_email || '—'}
          </div>
          <div className="flex flex-wrap items-stretch gap-3">
            {/* Wave 3.23 — Settlement totals.
                Total Bill = cash payable to goldsmith (labour + cash gold loss).
                Total Cost = labour + gold loss VALUE (the user's true product
                cost, even if loss is settled in extra gold instead of cash).
                When loss is settled in cash, both tiles converge — to avoid a
                confusing duplicate, we render the Total Cost tile only when
                the values differ (i.e., loss is in gold). */}
            {(() => {
              const totalBill = Number(j.total_amount || 0);
              const totalCost = Number(j.total_product_labour_inr || 0);
              const showCost = totalCost > 0 && Math.abs(totalCost - totalBill) > 0.5;
              return (
                <>
                  {showCost && (
                    <div
                      className="rounded-md border border-slate-200 bg-slate-50 px-4 py-2 text-right min-w-[180px]"
                      data-testid="jewellery-total-cost-tile"
                      title="Labour + value of the gold loss the goldsmith retained as cost-in-kind. Surface this so you see the true product cost even when the loss is settled in gold."
                    >
                      <div className="text-[11px] uppercase tracking-wide text-slate-500">
                        Total Cost <span className="normal-case text-[10px] text-slate-400">(Labour + Gold Loss Value)</span>
                      </div>
                      <div className="text-2xl font-mono text-[var(--ink-navy)]" data-testid="jewellery-detail-total-cost">
                        {formatINR(totalCost)}
                      </div>
                    </div>
                  )}
                  <div
                    className="rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-right min-w-[180px]"
                    data-testid="jewellery-total-bill-tile"
                    title="Cash payable to the goldsmith. Excludes gold loss when the loss is settled in extra gold (the goldsmith keeps that gold)."
                  >
                    <div className="text-[11px] uppercase tracking-wide text-amber-700">Total Bill</div>
                    <div className="text-2xl font-mono text-amber-700" data-testid="jewellery-detail-total">
                      {formatINR(totalBill)}
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      </Card>
      </section>
      )}

      <PdfDownloadDialog
        open={pdfOpen}
        onOpenChange={setPdfOpen}
        title={`Bill of Material — ${j.jewellery_no}`}
        description="Choose whether to include the company logo and name on this PDF, and pick the level of detail for the raw material section."
        companyId={effectiveCompanyId}
        endpoint={`/jewellery/${j.id}/bom/pdf`}
        filename={`BOM_${j.jewellery_no}.pdf`}
        mode="download"
        extraParams={{ rml_form: bomRmlForm }}
        extras={(
          <div
            className="rounded-md border border-[var(--divider)] bg-[var(--paper-warm)] p-3"
            data-testid="bom-rml-form-toggle-card"
          >
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-medium text-[var(--ink-navy)]">
                  Raw material ledger detail
                </div>
                <div className="text-[11px] text-slate-500">
                  {bomRmlForm === 'collapsed'
                    ? 'Compact: one line per material used (with Avg Size for non-gold).'
                    : 'Expanded: each packet/purity gets its own drill-down ledger (Issued / Consumed / Returned with running balance).'}
                </div>
              </div>
              <div
                className="inline-flex items-center rounded-md border border-[var(--divider)] overflow-hidden text-xs shrink-0"
                data-testid="bom-rml-form-toggle"
              >
                <button
                  type="button"
                  onClick={() => setBomRmlForm('collapsed')}
                  className={`px-3 py-1.5 transition-colors ${
                    bomRmlForm === 'collapsed'
                      ? 'bg-[var(--ink-navy)] text-white'
                      : 'bg-white text-slate-700 hover:bg-slate-50'
                  }`}
                  data-testid="bom-rml-form-collapsed"
                >
                  Collapsed
                </button>
                <button
                  type="button"
                  onClick={() => setBomRmlForm('expanded')}
                  className={`px-3 py-1.5 transition-colors ${
                    bomRmlForm === 'expanded'
                      ? 'bg-[var(--ink-navy)] text-white'
                      : 'bg-white text-slate-700 hover:bg-slate-50'
                  }`}
                  data-testid="bom-rml-form-expanded"
                >
                  Expanded
                </button>
              </div>
            </div>
          </div>
        )}
      />
    </div>
  );
}

function Stat({ label, value, icon, mono, highlight, muted }) {
  return (
    <div
      className={`rounded-md border px-3 py-2 ${
        highlight
          ? 'bg-amber-50 border-amber-200'
          : muted
          ? 'bg-slate-50/50 border-slate-100'
          : 'bg-slate-50 border-slate-200'
      }`}
    >
      <div className="text-[10px] uppercase tracking-wide text-slate-500 flex items-center gap-1">
        {icon}{label}
      </div>
      <div
        className={`text-sm ${mono ? 'font-mono' : ''} ${
          highlight ? 'text-amber-800 font-medium' : muted ? 'text-slate-400' : 'text-[var(--ink-navy)]'
        }`}
      >
        {value}
      </div>
    </div>
  );
}
